import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchFaqList, editFaq, updateFaq, trashFaq } from "../../Adapters/ApiAdapter";

const FaqList = () => {
    const [faqList, setFaqList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editItem, setEditItem] = useState(null);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [loadingAction, setLoadingAction] = useState(false);

    // Fetch FAQs on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchFaqList();
                if (response?.data) {
                    setFaqList((response.data).sort(
                        (a, b) => new Date(b.created_at) - new Date(a.created_at)
                    )
                    );
                } else {
                    toast.error("Failed to fetch FAQ list.");
                }
            } catch (error) {
                toast.error("An error occurred while fetching FAQs.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleEdit = async (id) => {
        try {
            const response = await editFaq(id);
            console.log("Edit Response:", response); // Debugging

            if (response?.data) {
                const faqData = Array.isArray(response.data) ? response.data[0] : response.data; // Handle array case

                setEditItem({
                    id: faqData.id,
                    question: faqData.question || "",
                    answer: faqData.answer || "",
                });
            } else {
                toast.error("Failed to load FAQ for editing.");
            }
        } catch (error) {
            toast.error("An error occurred while loading FAQ data.");
        }
    };


    const handleSaveEdit = async () => {
        setLoadingAction(true);
        try {
            const response = await updateFaq(editItem.id, {
                question: editItem.question,
                answer: editItem.answer,
            });
            if (response?.data) {
                setFaqList((prev) =>
                    prev.map((item) => (item.id === editItem.id ? { ...item, ...response.data } : item))
                );
                setEditItem(null);
                toast.success("FAQ updated successfully!");
            } else {
                toast.error("Failed to save changes.");
            }
        } catch (error) {
            toast.error("An error occurred while saving changes.");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleDelete = async (id) => {
        setLoadingAction(true);
        try {
            await trashFaq(id);
            setFaqList((prev) => prev.filter((item) => item.id !== id));
            toast.success("FAQ deleted successfully!");
            setDeleteItemId(null);
        } catch (error) {
            toast.error("Failed to delete FAQ.");
        } finally {
            setLoadingAction(false);
        }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 100 },
        { field: "question", headerName: "Question", width: 300 },
        { field: "answer", headerName: "Answer", width: 400 },
        {
            field: "actions",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(params.row.id)}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => setDeleteItemId(params.row.id)}
                        style={{ marginLeft: "8px" }}
                    >
                        Delete
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box m="20px">
            <ToastContainer />
            <Typography variant="h4" gutterBottom>
                FAQ List
            </Typography>

            {loading ? (
                <CircularProgress />
            ) : (
                <DataGrid rows={faqList} columns={columns} autoHeight />
            )}

            {/* Edit Dialog */}
            {editItem && (
                <Dialog open onClose={() => setEditItem(null)}>
                    <DialogTitle>Edit FAQ</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Question"
                            value={editItem.question}
                            onChange={(e) =>
                                setEditItem({ ...editItem, question: e.target.value })
                            }
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Answer"
                            multiline
                            rows={4}
                            value={editItem.answer}
                            onChange={(e) =>
                                setEditItem({ ...editItem, answer: e.target.value })
                            }
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditItem(null)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveEdit}
                            disabled={loadingAction}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Delete Confirmation */}
            {deleteItemId && (
                <Dialog open onClose={() => setDeleteItemId(null)}>
                    <DialogTitle>Delete Confirmation</DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete this FAQ?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteItemId(null)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleDelete(deleteItemId)}
                            disabled={loadingAction}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default FaqList;